
import { User, browserLocalPersistence, onIdTokenChanged, setPersistence, signOut } from 'firebase/auth';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { firebaseAuth } from '@/lib/firebase/client';

type AuthContextValue = {
  user: User | null;
  idToken: string | null;
  loading: boolean;
  error: string | null;
  logout: () => Promise<void>;
  refreshIdToken: () => Promise<string | null>;
  clearError: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function getReadableError(error: unknown) {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPersistence(firebaseAuth, browserLocalPersistence).catch((err) => {
      console.error('Failed to set Firebase auth persistence', err);
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setIdToken(null);
        setLoading(false);
        return;
      }

      try {
        const token = await firebaseUser.getIdToken();
        setUser(firebaseUser);
        setIdToken(token);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch Firebase ID token', err);
        setError(getReadableError(err));
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const logout = useCallback(async () => {
    setError(null);
    await signOut(firebaseAuth).catch((err) => {
      setError(getReadableError(err));
      throw err;
    });
  }, []);

  const refreshIdToken = useCallback(async () => {
    if (!firebaseAuth.currentUser) {
      return null;
    }

    try {
      const token = await firebaseAuth.currentUser.getIdToken(true);
      setIdToken(token);
      return token;
    } catch (err) {
      setError(getReadableError(err));
      throw err;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(() => {
    return {
      user,
      idToken,
      loading,
      error,
      logout,
      refreshIdToken,
      clearError,
    };
  }, [user, idToken, loading, error, logout, refreshIdToken, clearError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
